import { useCallback, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { SubmitFormContext } from '../context';
import { MIME_CONFIG, VIDEO_MIME } from '@wix/da-shared-react/pkg/utils/mime';
import { useFileUpload } from '@wix/da-shared-react/pkg/utils/hooks/useFileUpload';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { getDeviationType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import { UploadType } from '@wix/da-shared-react/pkg/redux/uploads/types';
import type { FileUploadOptions } from '@wix/da-shared-react/pkg/utils/hooks/useFileUpload';
import { validator } from '../layouts/common/FilePreview/validation';

export interface UseFilePreviewMainUploadsOptions {
  onReplaceOriginalSuccess?: () => void;
}

export function useFilePreviewMainUploads(options: UseFilePreviewMainUploadsOptions = {}) {
  const { onReplaceOriginalSuccess } = options;
  const dispatch = useDispatch();
  const submitFormContext = useContext(SubmitFormContext);
  const { initResponse, params, refreshInitData, registerValidator, unregisterValidator } =
    submitFormContext ?? {};
  const { errors } = useFormContext();

  const needsPreview = initResponse?.requiresPreview && !initResponse.hasCustomPreview;
  const deviation = initResponse?.deviation;
  const isFilm = getDeviationType(deviation) === DeviationTypes.FILM;

  useEffect(() => {
    if (needsPreview) {
      registerValidator?.('filePreview', validator);
    }
    return () => {
      if (needsPreview) {
        unregisterValidator?.('filePreview');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsPreview]);

  const hasError = !!errors['filePreview'];

  const onSuccess = useCallback(() => {
    refreshInitData?.();
  }, [refreshInitData]);

  const onSuccessOriginal = useCallback(() => {
    refreshInitData?.();
    onReplaceOriginalSuccess?.();
  }, [refreshInitData, onReplaceOriginalSuccess]);

  const onFailure = useCallback(
    (payload: unknown, error: { message: string }) => {
      dispatch(addErrorNotification(error.message));
    },
    [dispatch]
  );

  const handleDrop = useCallback(() => {
    return true;
  }, []);

  const handleReplaceOrig = useCallback(() => {
    return true;
  }, []);

  const { input, activeUploads, getDropzoneRootProps, isDragging } = useFileUpload({
    id: 'submit-preview-replacement',
    uploadType: UploadType.DEVIATION_REPLACE_PREVIEW,
    uploadParams: {
      deviationId: params?.deviationId,
    },
    allowMultiple: false,
    allowDragAndDrop: true,
    acceptedMimeTypes: MIME_CONFIG.forFilePreview,
    onDrop: handleDrop,
    onSuccess,
    onFailure,
  } as FileUploadOptions);

  const {
    input: inputOrig,
    showFilePicker: showOrigFilePicker,
    activeUploads: activeOrigUploads,
  } = useFileUpload({
    id: 'submit-orig-replacement',
    uploadType: UploadType.DEVIATION_REPLACE_ORIGINAL,
    uploadParams: {
      deviationId: params?.deviationId,
    },
    allowMultiple: false,
    allowDragAndDrop: false,
    acceptedMimeTypes: isFilm ? VIDEO_MIME : MIME_CONFIG.forDeviationSubmit,
    onDrop: handleReplaceOrig,
    onSuccess: onSuccessOriginal,
    onFailure,
  } as FileUploadOptions);

  return {
    needsPreview,
    isFilm,
    deviation,
    hasError,
    input,
    inputOrig,
    showOrigFilePicker,
    getDropzoneRootProps,
    isDragging,
    activeUploads,
    activeOrigUploads,
    initResponse,
  };
}
